import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import {
  ConditionalRule,
  CustomField,
  DocumentGenerationEntryForm,
  DocumentTemplate
} from '@ee/common/models';
import { FormService } from '@ee/common/services';
import { of, Subscription } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import {
  DocumentRuleDialogComponent,
  DocumentRuleDialogData,
  DocumentRuleDialogResult
} from './document-rule-dialog.component';

/**
 * 'selection' = dropdown + visibility toggle only (Action Center).
 * 'rule'      = also exposes the shared Condition_Builder per entry (Workflow Editor).
 */
export type DocumentGenerationMode = 'selection' | 'rule';

/**
 * How an entry's template id resolved:
 * - `resolved` — the template was found and can be named.
 * - `missing`  — templates loaded successfully AND the id resolved to nothing available to this org
 *                (deleted, or owned by another org). Safe to tell the user it is gone.
 * - `unknown`  — we could not establish either way (the templates load failed or timed out, or the
 *                resolve request is still in flight). Never claim deletion in this state: a network
 *                blip must not read as "your template was deleted".
 */
export type DocumentGenerationRowState = 'resolved' | 'missing' | 'unknown';

/** One rendered row, always present for every entry in the array. */
export interface DocumentGenerationRow {
  control: DocumentGenerationEntryForm;
  index: number;
  templateId: string;
  template: DocumentTemplate | null;
  state: DocumentGenerationRowState;
}

/**
 * The unified Document_Generation_Control (`ee-document-generation-control`) that
 * replaces the legacy two-dropdown `ee-document-selector`. It renders one document
 * selector dropdown plus, per selected entry, a Visibility_Toggle mirroring the
 * activity-log visibility toggle, and — in `rule` mode only — an embedded
 * `ee-condition-builder`.
 *
 * The host owns the `FormArray<DocumentGenerationEntryForm>` and passes it in via
 * `entries`; this control mutates the array (add / remove / toggle / rule) but never
 * reassigns it.
 *
 * **Every entry gets a row.** The control deliberately separates *what may be added* (the picker,
 * populated from the org's enabled templates) from *what is already referenced* (the rows). A stored
 * entry can legitimately point at a template the picker cannot offer — a disabled one, or a public /
 * built-in one carried over by cloning a built-in workflow — and those ids are resolved through
 * `FormService.resolveForms` so they can still be named. Anything genuinely unresolvable renders as an
 * explicit unavailable row with a working remove button. Hiding such rows is what previously made the
 * workflow editor disagree with the step summary's document count.
 */
@Component({
  selector: 'ee-document-generation-control',
  templateUrl: './document-generation-control.component.html',
  styleUrls: ['./document-generation-control.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class DocumentGenerationControlComponent implements OnInit, OnDestroy {
  /** Selection-only (Action Center) vs. rule mode (Workflow Editor). */
  @Input() mode: DocumentGenerationMode = 'selection';
  /** Uniform cap applied everywhere (Req 1.8). */
  @Input() maxEntries = 10;
  /** Field references available for rule building (rule mode only). */
  @Input() sourceFields: CustomField[] = [];
  /**
   * Initial `hide_from_client` value for newly added entries (EE-209). Sourced from the org's
   * "Default document visibility" case setting; the user can still toggle it per entry.
   */
  @Input() defaultHideFromClient = false;
  /**
   * View-only mode. Set by hosts that disable the surrounding form — e.g. the workflow editor viewing a
   * built-in workflow. Disables the add-picker and every per-row action. (Distinct from
   * [pickerUnavailable], which is about the templates load, not about permission to edit.)
   */
  @Input()
  set readOnly(value: boolean) {
    this.readOnlyValue = value;
    this.updatePickerEnabled();
  }

  get readOnly(): boolean {
    return this.readOnlyValue;
  }

  /**
   * Reactive form array of entries; owned by the host. Declared as a setter so that re-binding to a
   * different step's array re-subscribes and invalidates the memoized rows.
   */
  @Input({ required: true })
  set entries(value: FormArray<DocumentGenerationEntryForm>) {
    if (this.entriesValue === value) {
      return;
    }
    this.entriesValue = value;
    this.entriesSubscription?.unsubscribe();
    this.entriesSubscription = value?.valueChanges.subscribe(() => this.onEntriesChanged());
    this.onEntriesChanged();
  }

  get entries(): FormArray<DocumentGenerationEntryForm> {
    return this.entriesValue;
  }

  /** Org-scoped, enabled templates returned by the Generation_Service — the add-picker's source. */
  templates: DocumentTemplate[] = [];
  /** Becomes true once the templates load resolves (success, empty, error, or timeout). */
  templatesLoaded = false;
  /** Req 1.6 — load failed or timed out. */
  templatesLoadError = false;
  /** Req 1.7 — zero templates for the current organization. */
  noTemplatesAvailable = false;

  /**
   * Search/query model for the Material autocomplete used to add a document. Holds the
   * free-text filter; selecting an option adds the entry and the field is cleared.
   */
  readonly searchControl = new FormControl<string>('', { nonNullable: true });

  private entriesValue!: FormArray<DocumentGenerationEntryForm>;
  private entriesSubscription: Subscription | null = null;
  private readOnlyValue = false;

  /**
   * id -> template for everything we can name: the picker's templates plus anything additionally
   * resolved for existing entries. A map (not a repeated `.find()`) because the rows getter is bound in
   * the template and therefore runs on every change-detection tick.
   */
  private readonly templateById = new Map<string, DocumentTemplate>();
  /** Ids that the resolve request confirmed resolve to nothing. */
  private readonly confirmedMissingIds = new Set<string>();
  /** True once the resolve pass for the current entry set has settled. */
  private resolveSettled = false;

  private rowsCache: DocumentGenerationRow[] = [];
  private rowsDirty = true;

  private groupsCache: { type: string; items: DocumentTemplate[] }[] = [];
  private groupsCacheKey: string | null = null;
  /** Bumped whenever the templates or the entry set change, to invalidate the picker's group cache. */
  private catalogVersion = 0;

  private readonly subscriptions: Subscription[] = [];

  /** Req 1.6 — load is considered failed if templates do not arrive within 10 seconds. */
  private static readonly LOAD_TIMEOUT_MS = 10000;

  constructor(
    private formService: FormService,
    private dialog: MatDialog,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.formService.getAllForms().pipe(
        timeout(DocumentGenerationControlComponent.LOAD_TIMEOUT_MS),
        catchError(() => {
          this.templatesLoadError = true;
          return of<DocumentTemplate[]>([]);
        })
      ).subscribe((templates) => {
        if (!this.templatesLoadError) {
          this.templates = templates ?? [];
          this.noTemplatesAvailable = this.templates.length === 0;
          this.templates.forEach((t) => this.templateById.set(t.id, t));
        }
        this.templatesLoaded = true;
        this.invalidateRows();

        // Reflect the load result in the picker's enabled state (Req 1.6/1.7).
        this.updatePickerEnabled();

        // Anything the picker's list could not account for may still be a legitimate reference
        // (disabled, or public/built-in). Resolve those so the rows can be named.
        this.resolveUnnamedEntries();

        // The control uses default change detection but is rendered inside OnPush hosts
        // (e.g. CompleteWorkflowStepComponent). Mark the ancestor chain dirty so the
        // async template load is reflected without needing an unrelated user event.
        this.changeDetectorRef.markForCheck();
      })
    );

    this.subscriptions.push(
      this.searchControl.valueChanges.subscribe(() => this.invalidatePickerGroups())
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.entriesSubscription?.unsubscribe();
  }

  /**
   * Whether the dropdown is non-selectable because of the templates load itself (error, or the org has
   * no templates). Not the same as [readOnly].
   */
  get pickerUnavailable(): boolean {
    return this.templatesLoadError || this.noTemplatesAvailable;
  }

  /** Retained name for the picker-unavailable state. */
  get disabled(): boolean {
    return this.pickerUnavailable;
  }

  /** Whether the 10-entry cap has been reached (Req 1.9). */
  get atCap(): boolean {
    return this.entries.length >= this.maxEntries;
  }

  /** Placeholder text reflecting the dropdown's current state. */
  get placeholder(): string {
    if (this.templatesLoadError) {
      return 'Document templates could not be loaded';
    }
    if (this.noTemplatesAvailable) {
      return 'No document templates available';
    }
    return 'Add a document…';
  }

  /**
   * Templates offered by the add-picker: the org's enabled templates that are not already selected, so
   * the same template id can be chosen at most once (Req 1.4). Deliberately narrower than what the rows
   * can display — a referenced template that is disabled or public is nameable but not re-addable.
   */
  get availableTemplates(): DocumentTemplate[] {
    const selected = new Set(this.entries.controls.map((c) => c.controls.document_template_id.value));
    return this.templates.filter((t) => !selected.has(t.id));
  }

  /**
   * One row per entry, in array order — never filtered. This is the configuration truth: an entry the
   * step holds is represented here whether or not its template could be resolved.
   *
   * Memoized: this is read on every change-detection tick via [displayRows].
   */
  get allRows(): DocumentGenerationRow[] {
    if (this.rowsDirty || this.rowsCache.length !== this.entries.length) {
      this.rowsCache = this.buildRows();
      this.rowsDirty = false;
    }
    return this.rowsCache;
  }

  /**
   * The rows the template renders, which depends on what the surface is *for*:
   *
   * - **`rule` mode (workflow editor)** — every entry, including unavailable ones. This is the
   *   configuration surface: you cannot fix or remove what you cannot see, and hiding entries here is
   *   exactly what made the editor disagree with the step summary's document count.
   * - **`selection` mode (step completion / Action Center / bulk)** — only entries that will actually
   *   produce a document. A deleted or disabled template generates nothing, so listing it as a document
   *   about to be generated is noise at best and a false promise at worst.
   *
   * The hidden entries are also pruned from the array (see [pruneNonGeneratableEntries]), so in
   * selection mode *hidden* and *not generated* stay the same set. Hiding a row while still submitting
   * it would recreate the original bug inverted — a document produced that was never shown.
   */
  get displayRows(): DocumentGenerationRow[] {
    const rows = this.allRows;
    if (this.mode === 'rule') {
      return rows;
    }
    return rows.filter((row) => this.isGeneratable(row) || row.state === 'unknown');
  }

  /**
   * Whether this entry will produce a document. A template that resolved but is **disabled** will not:
   * disabling means "stop using this", and the backend skips it on both the eviction and Case paths.
   */
  private isGeneratable(row: DocumentGenerationRow): boolean {
    return !!row.template && !row.template.disabled;
  }

  /** True when at least one row could not be resolved, so the template can show a single explanation. */
  get hasUnavailableRows(): boolean {
    return this.displayRows.some((row) => row.state !== 'resolved');
  }

  private buildRows(): DocumentGenerationRow[] {
    return this.entries.controls.map((control, index) => {
      const templateId = control.controls.document_template_id.value;
      const template = this.templateById.get(templateId) ?? null;
      let state: DocumentGenerationRowState;
      if (template) {
        state = 'resolved';
      } else if (this.templatesLoadError || !this.templatesLoaded || !this.resolveSettled) {
        // We genuinely do not know yet — do not accuse anything of being deleted.
        state = 'unknown';
      } else {
        state = this.confirmedMissingIds.has(templateId) ? 'missing' : 'unknown';
      }
      return { control, index, templateId, template, state };
    });
  }

  /**
   * Ask the backend to name any referenced template the picker's list did not cover. Issues no request
   * at all when every entry already resolved, so a healthy configuration costs nothing.
   */
  private resolveUnnamedEntries(): void {
    if (!this.templatesLoaded || this.templatesLoadError || !this.entriesValue) {
      return;
    }

    const unnamed = Array.from(new Set(
      this.entriesValue.controls
        .map((c) => c.controls.document_template_id.value)
        .filter((id) => !!id && !this.templateById.has(id) && !this.confirmedMissingIds.has(id))
    ));

    if (!unnamed.length) {
      this.resolveSettled = true;
      this.invalidateRows();
      this.pruneNonGeneratableEntries();
      return;
    }

    this.subscriptions.push(
      this.formService.resolveForms(unnamed).pipe(
        catchError(() => of<DocumentTemplate[]>([]))
      ).subscribe((resolved) => {
        (resolved ?? []).forEach((t) => this.templateById.set(t.id, t));
        // Whatever came back short is genuinely unavailable to this org.
        unnamed.forEach((id) => {
          if (!this.templateById.has(id) && this.formService.isFormUnresolvable(id)) {
            this.confirmedMissingIds.add(id);
          }
        });
        this.resolveSettled = true;
        this.invalidateRows();
        this.pruneNonGeneratableEntries();
        this.changeDetectorRef.markForCheck();
      })
    );
  }

  /**
   * In `selection` mode only, drop entries that cannot produce a document so the array submitted for
   * generation matches exactly what the user was shown.
   *
   * Never runs in `rule` mode — that array is the step's stored configuration, and pruning it would
   * silently rewrite the workflow. Never runs while the state is `unknown` either: a failed templates
   * load must not be mistaken for "these templates are gone" and quietly drop the user's selection.
   */
  private pruneNonGeneratableEntries(): void {
    if (this.mode !== 'selection' || this.readOnly) {
      return;
    }
    if (!this.templatesLoaded || this.templatesLoadError || !this.resolveSettled) {
      return;
    }

    // Reverse order so each removal leaves the remaining indices valid.
    const doomed = this.allRows
      .filter((row) => row.state !== 'unknown' && !this.isGeneratable(row))
      .map((row) => row.index)
      .reverse();

    if (!doomed.length) {
      return;
    }

    doomed.forEach((index) => this.entries.removeAt(index, { emitEvent: false }));
    this.invalidateRows();
    this.updatePickerEnabled();
  }

  private onEntriesChanged(): void {
    this.invalidateRows();
    // A newly seeded entry set (e.g. step completion) can introduce ids the picker cannot name.
    this.resolveUnnamedEntries();
  }

  private invalidateRows(): void {
    this.rowsDirty = true;
    this.invalidatePickerGroups();
  }

  private invalidatePickerGroups(): void {
    this.catalogVersion++;
    this.groupsCacheKey = null;
  }

  /**
   * Templates available to add, grouped by type, filtered by the current search text and
   * excluding already-selected templates. Drives the autocomplete option list.
   *
   * Memoized on the search text and a catalog version, because this is called from the template's
   * `@for` and would otherwise rebuild the whole grouping on every change-detection tick.
   */
  filteredGroups(): { type: string; items: DocumentTemplate[] }[] {
    const query = (this.searchControl.value ?? '').trim().toLowerCase();
    const key = `${this.catalogVersion}|${query}`;
    if (this.groupsCacheKey === key) {
      return this.groupsCache;
    }

    const matches = this.availableTemplates.filter(
      (t) => !query || (t.name ?? '').toLowerCase().includes(query)
    );
    const byType = new Map<string, DocumentTemplate[]>();
    matches.forEach((t) => {
      const type = t.type ?? 'Other';
      const group = byType.get(type);
      if (group) {
        group.push(t);
      } else {
        byType.set(type, [t]);
      }
    });

    this.groupsCache = Array.from(byType.entries()).map(([type, items]) => ({ type, items }));
    this.groupsCacheKey = key;
    return this.groupsCache;
  }

  /** Whether the autocomplete currently has any selectable options. */
  get hasOptions(): boolean {
    return this.availableTemplates.length > 0;
  }

  /**
   * Add the chosen template when an autocomplete option is selected, then clear the
   * search field so the picker is ready for the next add.
   */
  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const templateId = event.option.value as string;
    if (templateId) {
      this.addEntry(templateId);
    }
    this.searchControl.setValue('');
  }

  /** Add a single entry for the template, deduped and capped, defaulting hide_from_client to the org default. */
  addEntry(templateId: string): void {
    if (!templateId || this.atCap || this.readOnly) {
      return; // Req 1.9 — cap guard
    }
    if (this.entries.controls.some((c) => c.controls.document_template_id.value === templateId)) {
      return; // Req 1.4 — dedupe by template id
    }
    this.entries.push(this.createEntryForm(templateId)); // Req 1.2 / 2.2
    this.updatePickerEnabled();
  }

  /** Remove the single entry at the given index (Req 1.3). Also how an unavailable row is cleaned up. */
  removeEntry(index: number): void {
    if (this.readOnly) {
      return;
    }
    this.entries.removeAt(index);
    this.updatePickerEnabled();
  }

  /**
   * Enable the picker only when templates are loaded, the host is editable, and the cap has not been
   * reached (Req 1.6 load error / 1.7 no templates → disabled; Req 1.9 cap → can't add more).
   */
  private updatePickerEnabled(): void {
    const shouldDisable = this.pickerUnavailable || this.atCap || this.readOnly;
    if (shouldDisable && this.searchControl.enabled) {
      this.searchControl.disable({ emitEvent: false });
    } else if (!shouldDisable && this.searchControl.disabled) {
      this.searchControl.enable({ emitEvent: false });
    }
  }

  /** Flip the visibility of one entry, leaving every other entry unchanged (Req 2.3/2.4/2.7). */
  toggleVisible(index: number): void {
    if (this.readOnly) {
      return;
    }
    const control = this.entries.at(index).controls.hide_from_client;
    control.setValue(!control.value);
  }

  /** Whether the entry has a conditional rule configured. */
  hasRule(entry: DocumentGenerationEntryForm): boolean {
    return !!entry.controls.conditional_rule.value?.conditions?.length;
  }

  /**
   * A short human-readable summary of the entry's rule, e.g.
   * "Only when Property State is equal to GA and Past Due Rent is greater than 0" —
   * or `null` when no rule is set.
   */
  ruleSummary(entry: DocumentGenerationEntryForm): string | null {
    const conditions = entry.controls.conditional_rule.value?.conditions;
    if (!conditions?.length) {
      return null;
    }
    const parts = conditions.map((condition) => {
      const fieldLabel = condition.source_field?.label ?? condition.source_field?.field ?? 'field';
      const operator = this.readableOperator(condition.operator);
      if (condition.operator === 'blank') {
        return `${fieldLabel} ${operator}`;
      }
      const value = Array.isArray(condition.value) ? condition.value.join(', ') : condition.value;
      return `${fieldLabel} ${operator} ${value ?? ''}`.trim();
    });
    return `Only when ${parts.join(' and ')}`;
  }

  private readableOperator(operator: string | null | undefined): string {
    switch (operator) {
      case '==': return 'is equal to';
      case '<=': return 'is less than or equal to';
      case '>=': return 'is greater than or equal to';
      case '>': return 'is greater than';
      case '<': return 'is less than';
      case 'in': return 'is one of';
      case 'none': return 'is not one of';
      case 'blank': return 'is empty';
      case 'contains': return 'contains';
      case 'not_contains': return 'does not contain';
      case 'starts_with': return 'starts with';
      case 'ends_with': return 'ends with';
      default: return operator ?? '';
    }
  }

  /**
   * Open the rule dialog for the entry. On save, store the rule on the entry; on remove,
   * clear it; on cancel, leave it unchanged (Req 4.1, 4.4, 4.6).
   */
  openRuleDialog(entry: DocumentGenerationEntryForm): void {
    if (this.readOnly) {
      return;
    }
    const ref = this.dialog.open<DocumentRuleDialogComponent, DocumentRuleDialogData, DocumentRuleDialogResult>(
      DocumentRuleDialogComponent,
      {
        width: '1000px',
        maxWidth: '96vw',
        data: { rule: entry.controls.conditional_rule.value, sourceFields: this.sourceFields }
      }
    );
    this.subscriptions.push(
      ref.afterClosed().subscribe((result) => {
        if (result) {
          entry.controls.conditional_rule.setValue(result.rule);
          this.changeDetectorRef.markForCheck();
        }
      })
    );
  }

  private createEntryForm(templateId: string): DocumentGenerationEntryForm {
    return new FormGroup({
      document_template_id: new FormControl(templateId, { nonNullable: true }),
      hide_from_client: new FormControl(this.defaultHideFromClient, { nonNullable: true }),
      conditional_rule: new FormControl<ConditionalRule | null>(null)
    });
  }
}
